import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePublicCrews } from '../../hooks/usePublicCrews';
import axiosService from '../../services/axios.service';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import { H5 } from '../ui';

const palette = ['#5865F2', '#3ba55d', '#faa81a', '#9c84ec', '#e91e8c', '#1e88e5'];
const getAccentColor = (name: string) => {
    const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return palette[hash % palette.length];
};

export const BrowsePublicCrews: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [joiningSlug, setJoiningSlug] = useState<string | null>(null);
    const [joinError, setJoinError] = useState<string | null>(null);
    const { data, isLoading, error } = usePublicCrews(page, 10, search);

    const handleJoin = async (e: React.MouseEvent, slug: string) => {
        e.stopPropagation();
        setJoiningSlug(slug);
        setJoinError(null);
        try {
            await axiosService.post(`/c/${slug}/join`);
            await queryClient.invalidateQueries({ queryKey: ['crews', 'list'] });
            navigate(`/c/${slug}`);
        } catch (err: any) {
            setJoiningSlug(null);
            setJoinError(err?.response?.data?.error ?? 'Failed to join crew. Please try again.');
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    if (isLoading) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading public crews...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                Failed to load public crews. Please try again later.
            </Alert>
        );
    }

    const crews = data?.data || [];
    const pagination = data?.pagination;

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Browse Public Crews
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Discover and join crews that allow public access
                </Typography>
            </Box>

            <Box component="form" onSubmit={handleSearch}>
                <Stack direction="row" spacing={1}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search crews by name or description..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                    />
                    <Button variant="contained" type="submit">
                        Search
                    </Button>
                </Stack>
            </Box>

            {joinError && <Alert severity="error" onClose={() => setJoinError(null)}>{joinError}</Alert>}
            {crews.length === 0 ? (
                <Alert severity="info">
                    {search ? 'No crews found matching your search.' : 'No public crews available at this time.'}
                </Alert>
            ) : (
                <>
                    <Box>
                        <Grid container spacing={2}>
                            {crews.map((crew) => (
                                <Grid item xs={12} sm={6} md={4} key={crew.slug}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
                                        }}
                                        onClick={(e) => handleJoin(e, crew.slug)}
                                    >
                                        <Box
                                            sx={{
                                                height: 140,
                                                background: crew.headerImage
                                                    ? `url(${crew.headerImage}) center / cover`
                                                    : `linear-gradient(135deg, ${getAccentColor(crew.name)}bb 0%, ${getAccentColor(crew.name)} 100%)`,
                                            }}
                                        />
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <H5 sx={{ mb: 0.5 }}>{crew.name}</H5>
                                            {crew.memberCount !== undefined && (
                                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                                                    <PeopleOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                    <Typography variant="caption" color="text.secondary">
                                                        {crew.memberCount.toLocaleString()} {crew.memberCount === 1 ? 'member' : 'members'}
                                                    </Typography>
                                                </Stack>
                                            )}
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {crew.description}
                                            </Typography>
                                        </CardContent>
                                        <CardContent sx={{ pt: 0 }}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                disabled={joiningSlug !== null}
                                                onClick={(e) => handleJoin(e, crew.slug)}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                {joiningSlug === crew.slug ? 'Joining…' : 'Join Crew'}
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                    {pagination && pagination.pages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={pagination.pages}
                                page={page}
                                onChange={handlePageChange}
                            />
                        </Box>
                    )}
                </>
            )}
        </Stack>
    );
};
